import React from "react"

import { useTranslation } from "react-i18next"

import { AdminPlayerBonus2, BonusHistory } from "services/bonus/type"

import { formatDateTime,formatFiatCurrency } from "util/formatter"

import { getDisplayName } from "../../helpers/bonus2.helpers"

interface BonusHistoryModalProps {
  playerBonus: AdminPlayerBonus2
  history: BonusHistory[]
  onClose: () => void
}

export const BonusHistoryModal: React.FC<BonusHistoryModalProps> = ({ playerBonus, history, onClose }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A1A2E] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#1A1A2E] p-6 border-b border-gray-700 z-10">
          <h2 className="text-2xl font-bold text-white">{t("bonus2.players.bonusHistoryTitle", { name: getDisplayName(playerBonus.bonus.name) })}</h2>
        </div>

        <div className="p-6">
          {history.length === 0 ? (
            <div className="text-center text-gray-400 py-8">{t("bonus2.players.noHistoryAvailable")}</div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="bg-[#0C0C1A] border border-gray-700 rounded p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-purple-400 font-semibold">{h.event_type}</span>
                      {h.previous_status && h.new_status && (
                        <span className="text-gray-400 text-sm ml-2">
                          ({h.previous_status} → {h.new_status})
                        </span>
                      )}
                    </div>
                    <span className="text-gray-400 text-xs">{formatDateTime(h.created_at)}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                    {h.balance_before && h.balance_after && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.balance")} </span>
                        <span className="text-white">
                          {formatFiatCurrency(Number(h.balance_before))} → {formatFiatCurrency(Number(h.balance_after))}
                        </span>
                      </div>
                    )}

                    {h.wagering_before && h.wagering_after && (
                      <div>
                        <span className="text-gray-400">{t("bonus2.players.wagering2")} </span>
                        <span className="text-white">
                          {formatFiatCurrency(Number(h.wagering_before))} → {formatFiatCurrency(Number(h.wagering_after))}
                        </span>
                      </div>
                    )}
                  </div>

                  {h.metadata && (
                    <div className="mt-2 text-xs">
                      <span className="text-gray-400">{t("bonus2.players.details")} </span>
                      <pre className="text-gray-300 mt-1 bg-[#1A1A2E] p-2 rounded overflow-x-auto">
                        {JSON.stringify(h.metadata, null, 2)}
                      </pre>
                    </div>
                  )}

                  <div className="text-xs text-gray-400 mt-2">Triggered by: {h.triggered_by}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-[#1A1A2E] p-6 border-t border-gray-700">
          <button onClick={onClose} className="w-full px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">
            {t("bonus2.players.close")}
          </button>
        </div>
      </div>
    </div>
  )
}
